import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, Tooltip, Typography } from "@mui/material";
import { CheckCircleRounded, EmojiEventsOutlined, HighlightOffRounded, HourglassEmptyRounded, LeaderboardRounded, PaletteOutlined, ReplayRounded, VideocamRounded } from "@mui/icons-material";
import { chipColor, tokens } from "../../theme";
import "./board.css";
import { BOARD_LAYOUT } from "../../boardLayout";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PVForms from "../../Custom/PVForms";
import { useDispatch, useSelector } from "react-redux";
import { addBot, analyticsInstanceSelector, checkAndJoinGame, connectedSelector, createChallenge, createGame, createGameWithBots, declineRematch, discardCard, fetchRanks, gameSelector, joinGame, makeMove, presenceSelector, requestRematch, sendFeedback, setCurrentUser, setPlayers, startGame, subscribeToGame, trackGame, unsubscribeFromGame } from "../../services/GameSlice";
import { challengeLink, getSocket, resultLink } from "../../socket";
import ShareButtons from "../Share/ShareButtons";
import ReplayClip from "../Share/ReplayClip";
import StartGame from "../Start/StartGame";
import { CallControls, CallDock, CallStage, CallStrip, IncomingCall, isInCall } from "../Call/CallPanel";
import useCall from "../../call/useCall";
import { autoJoinTarget, setAutoJoinTarget } from "../../call/CallSession";
import { getUserId } from "../../identity";
import { gamePath, setPrefersClassic } from "../../uiMode";
import AccountButton from "../../account/AccountButton";
import { signIn as signInWithGoogle } from "../../account/session";
import PVTable from "../../Custom/PVTable";
import { setBody, setToastId, setType } from "../../services/NotificationSlice";
import ColorPicker from "react-pick-color";
import { setUserId as analyticsUserId, logEvent } from "firebase/analytics";
import { Link } from "react-router-dom";

// One id for the removal toast means a second removal replaces the first
// rather than stacking another box on top of it.
const REMOVAL_TOAST = 'chip-removed';

const savedName = () => {
    try {
        return localStorage.getItem('playerName') || '';
    } catch (error) {
        return '';
    }
};

const REMATCH_STATUS = {
    host: 'Asked for the rematch',
    ready: 'Ready',
    declined: 'Declined',
    waiting: 'Deciding…',
    away: 'Left: a computer will play their seat'
};

const DIFFICULTY_LABEL = { easy: 'Easy', medium: 'Medium', hard: 'Hard' };

const PERIOD_LABEL = { day: 'Today', week: 'This week', month: 'This month', all: 'All time' };

/**
 * After a game against the computer: the player's place on each of the
 * level's boards. A loss still shows any place an earlier win holds, or what
 * it takes to get on the board.
 */
const RankStrip = ({ ranks, won, onSignIn }) => {
    const level = DIFFICULTY_LABEL[ranks.difficulty] || ranks.difficulty;
    const periods = ['day', 'week', 'month', 'all'].map(key => ({ key, ...ranks.periods[key] }));
    const ranked = periods.filter(p => p.rank);
    if (ranked.length === 0) {
        const leader = ranks.periods.day.leader || ranks.periods.all.leader;
        return (
            <p className="result-placement">
                <LeaderboardRounded fontSize="inherit"/>
                {leader
                    ? `Beat the computer to get on the ${level} leaderboard. The best win ${ranks.periods.day.leader ? 'today' : 'so far'} took ${leader.moves} moves.`
                    : `Beat the computer to get on the ${level} leaderboard. Nobody has yet.`}
            </p>
        );
    }
    return (
        <div className="rank-strip" aria-label={`Your place on the ${level} leaderboard`}>
            <span className="rank-strip-title">
                <LeaderboardRounded fontSize="inherit"/>
                {won ? `You're ranked on ${level}` : `Your best on ${level}`}
                {ranks.yourName && <span className="rank-strip-name">as {ranks.yourName}</span>}
                {!ranks.signedIn && onSignIn && <button type="button" className="rank-strip-signin" onClick={onSignIn}>Sign in to use your own name</button>}
            </span>
            <ul>
                {periods.map(p => (
                    <li key={p.key} className={p.rank === 1 ? 'first' : p.rank ? '' : 'none'}>
                        <span className="rank-strip-period">{PERIOD_LABEL[p.key]}</span>
                        <strong>{p.rank ? `#${p.rank}` : '–'}</strong>
                        <span className="rank-strip-of">{p.rank ? `of ${p.total}` : 'no win yet'}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
};

const Board = () => {

    const params = useParams();
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 600);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 600);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const [record, setRecord] = useState({});
    const [nameRecord, setNameRecord] = useState({});
    const [userId, setUserId] = useState('');
    const [selectedCard, setSelectedCard] = useState(null);
    const [removal, setRemoval] = useState(null);
    const previousBoard = useRef(null);
    const [feedbackForm, setFeedbackForm] = useState({});
    const [color, setColor] = useState(tokens.felt);
    const [opneColorPicker, setOpenColorPicker] = useState(false);
    // Which finished game's result dialog the player has closed.
    const [resultClosedFor, setResultClosedFor] = useState(null);
    // The rematch dialog can be put away; this is the rematch it was put away for.
    const [rematchHiddenFor, setRematchHiddenFor] = useState(null);
    // A cell clicked with a card that can't go there, when highlights are off.
    const [miss, setMiss] = useState(null);
    // Set once the call hook exists, further down: carries the call into a rematch.
    const carryCall = useRef(null);
    const analytics = useSelector(analyticsInstanceSelector);

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const game = useSelector(gameSelector);
    const presence = useSelector(presenceSelector);
    const connected = useSelector(connectedSelector);

    // A shuffled-board game brings its own layout.
    const data = game?.layout || BOARD_LAYOUT;

    useEffect(() => {
        if(Boolean(analytics) && Boolean(userId)){
            analyticsUserId(analytics, userId);
            logEvent(analytics, 'game_intialized', { userId })
        }
    }, [analytics, userId])

    // Establish the player identity once. Everything else keys off `userId`.
    useEffect(() => {
        const storedUserId = getUserId();
        setUserId(storedUserId);
        dispatch(setCurrentUser(storedUserId));
    }, [dispatch])

    // Subscribe to the game's room. The server pushes every later change, and
    // re-subscribes automatically after a reconnect, so there is no polling.
    useEffect(() => {
        if(!Boolean(userId)){
            return;
        }
        if(!Boolean(params.gameId)){
            setRecord({open: true, editRecord: {tabValue: 0}});
            return;
        }

        const socket = getSocket();
        const subscribe = () => dispatch(subscribeToGame(params.gameId, userId));

        subscribe();
        socket.on('connect', subscribe);

        return () => {
            socket.off('connect', subscribe);
            dispatch(unsubscribeFromGame());
        };
    }, [params.gameId, userId, dispatch])

    // When the rematch begins, every player still watching the finished game is
    // carried across to it. Anyone opening the old link later follows the same
    // hop, so a shared URL never dead-ends on a finished board.
    useEffect(() => {
        const rematchId = game?.rematch?.gameId ? String(game.rematch.gameId) : null;
        if(game?.rematch?.started && rematchId && rematchId !== params.gameId){
            // Someone who said no stays with the finished game; a computer
            // took their seat in the new one.
            if((game.rematch.declinedUserIds || []).includes(userId)){
                return;
            }
            // The server moved the call to the new game: keep every
            // connection as it is. A tab that was only about to rejoin the
            // call (after a reload) rejoins it in the new game instead.
            const carried = carryCall.current?.(rematchId);
            if(!carried && autoJoinTarget() === params.gameId){
                setAutoJoinTarget(rematchId);
            }
            navigate(`/${rematchId}`);
        }
    }, [game, params.gameId, navigate, userId])

    // A card picked up in the previous game must not carry into the next one.
    useEffect(() => {
        setSelectedCard(null);
    }, [params.gameId])

    // Ask for a name as soon as we know this player is not in the game yet.
    useEffect(() => {
        if(!Boolean(userId) || !Boolean(params.gameId) || !Boolean(game?.players)){
            return;
        }
        const alreadyJoined = game.players.some(item => item.userId === userId);
        const joinable = game.status === 'INIT' || game.status === 'WAITING';
        if(!alreadyJoined && joinable){
            setNameRecord(current => current?.open === true ? current : {open: true, editRecord: {userId: userId, name: savedName()}});
        }
    }, [game, userId, params.gameId])


    const startNewGame = (mode, values) => {
        const settings = {board: values.board, turnSeconds: values.turnSeconds, hints: values.hints};
        const opened = (created, event, extra = {}) => {
            setRecord({});
            navigate(`/${created._id}`);
            logEvent(analytics, event, {userId, gameId: created._id, board: values.board, turn_seconds: values.turnSeconds, ...extra});
        };
        if(mode === 'friends'){
            dispatch(createGame({roomName: values.roomName, noOfPlayers: values.noOfPlayers, settings},
                (created) => opened(created, 'game_created')));
        } else if(mode === 'challenge'){
            dispatch(createChallenge({name: values.name, userId, team: values.team, settings},
                (created) => opened(created, 'challenge_created')));
        } else if(mode === 'computer'){
            dispatch(createGameWithBots({name: values.name, userId, difficulty: values.difficulty, bots: 1, team: values.team, settings},
                (created) => opened(created, 'game_vs_computer_started', {difficulty: values.difficulty})));
        } else {
            dispatch(checkAndJoinGame({name: values.name, userId}, (created) => opened(created, 'matchmaking_joined')));
            dispatch(trackGame(params.gameId, userId));
        }
    }

    const join = () => {
        try {
            localStorage.setItem('playerName', nameRecord.editRecord?.name || '');
        } catch (error) { /* private mode */ }
        dispatch(joinGame(params.gameId, nameRecord.editRecord, () => setNameRecord({})));
        dispatch(trackGame(params.gameId, userId));
        logEvent(analytics, 'game_joined', {
            userId, gameId: params.gameId
        })
    }

    const start = () => {
        dispatch(startGame(params.gameId));
        logEvent(analytics, 'game_started', {
            userId, gameId: params.gameId
        })
    }

    const canStartGame = () => {
        if(game?.players?.length === game?.noOfPlayers
            && game?.players?.filter(item => item.team === undefined).length === 0){
            let count = {};
            game.players.forEach(item => {
                if(count[item.team]){
                    count[item.team] += 1;
                } else {
                    count[item.team] = 1;
                }
            })
            let values = Object.values(count);
            // Everyone on one colour passes the "even teams" check but is not a
            // game: every chip would belong to the same team.
            if(Object.keys(count).length >= 2 && values.every(val => val === values[0])){
                return true;
            }     
        }
        return false;
    }

    // A one-eyed jack deletes the chip from moves, so no field on the new game
    // says a removal happened — the only evidence is that a cell which was
    // occupied a moment ago is empty now. Comparing consecutive states is the
    // only way to catch it, and the player who did it is whoever was on turn in
    // the state we are comparing against.
    useEffect(() => {
        if(!game?._id){
            return;
        }
        const moves = game.moves || [];
        const now = {
            gameId: game._id,
            chips: new Map(moves.map(m => [m.pos, m])),
            onTurn: game.currentPlayer
        };
        const before = previousBoard.current;
        previousBoard.current = now;

        // Nothing to diff against on the first state, or after changing games.
        // A reload therefore misses a removal that happened while away, which
        // is the accepted cost of keeping this out of the server.
        if(!before || before.gameId !== game._id){
            return;
        }

        const takenOff = [...before.chips.keys()].filter(pos => !now.chips.has(pos));
        if(takenOff.length > 0){
            const pos = takenOff[0];
            const chip = before.chips.get(pos);
            const owner = chip.userId === userId ? 'your' : `${playerLabel(chip.userId)}'s`;
            setRemoval({ pos, team: chip.team });
            dispatch(setToastId(REMOVAL_TOAST));
            dispatch(setType('sticky'));
            dispatch(setBody(`${playerLabel(before.onTurn)} used a one-eyed jack to take ${owner} chip off the ${cardLabel(data[pos])}.`));
            return;
        }

        // Once the player has taken their own turn they have plainly seen the
        // board, so the marker and its toast have done their job.
        const iHaveMoved = moves.some(m => m.userId === userId && !before.chips.has(m.pos));
        if(removal && iHaveMoved){
            setRemoval(null);
            dispatch(setToastId(REMOVAL_TOAST));
            dispatch(setType('dismiss'));
            dispatch(setBody(' '));
        }
        // data is the fixed board layout and playerLabel only reads game, so
        // neither adds anything to react to — a new game state does.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [game, userId, removal, dispatch]);

    // Highlights are a setting of the game, chosen when it was made and the
    // same for everyone in it.
    const hintsOn = game?.settings?.hints !== false;

    const move = (pos) => {
        if(((!Boolean(game?.moves?.find(m => m.pos === pos)) && selectedCard === data[pos]) ||
            (['spades_jack', 'hearts_jack'].includes(selectedCard) && game?.moves?.find(m => m.pos === pos && m.team !== game?.players?.find(p => p.userId === userId)?.team?.toLowerCase())) ||
            (['clubs_jack', 'diamonds_jack'].includes(selectedCard) && !Boolean(game?.moves?.find(m => m.pos === pos)))) && game.currentPlayer === userId &&
            ![`0,0`, `0,9`, `9,0`, `9,9`].includes(pos)){
                setMiss(null);
                dispatch(makeMove(params.gameId, pos, userId, selectedCard, game));
        } else if(!hintsOn && selectedCard && game?.status === 'START' && game.currentPlayer === userId){
            // Without highlights nothing shows where a card can go, so a
            // wrong cell has to say so.
            setMiss({pos, at: Date.now()});
        }
    }

    useEffect(() => {
        if(!miss){
            return undefined;
        }
        const timer = setTimeout(() => setMiss(current => current === miss ? null : current), 2200);
        return () => clearTimeout(timer);
    }, [miss]);

    // A game against the computer, from this player's side: who they played
    // and whether they won. Null for any other game.
    const soloGame = (() => {
        const players = game?.players || [];
        const humans = players.filter(p => !p.isBot);
        if(game?.status !== 'COMPLETED' || humans.length !== 1 || humans[0].userId !== userId){
            return null;
        }
        const opponent = players.find(p => p.isBot && p.team !== humans[0].team);
        if(!opponent){
            return null;
        }
        const needed = game.noOfTeams === 3 ? 1 : 2;
        const won = !game.isDraw && (game.completedSequences?.[String(humans[0].team).toLowerCase()] || 0) >= needed;
        return {won, difficulty: opponent.difficulty || 'medium'};
    })();

    // At the end of a game against the computer: where this player now ranks
    // today, this week, this month and of all time. Asked a moment later,
    // once the server has recorded the game.
    const [ranks, setRanks] = useState(null);
    const soloLevel = soloGame ? soloGame.difficulty : null;
    useEffect(() => {
        setRanks(null);
        if(!soloLevel || !game?._id){
            return undefined;
        }
        let alive = true;
        const timer = setTimeout(() => {
            fetchRanks(soloLevel, userId)
                .then(data => alive && setRanks(data))
                .catch(() => {});
        }, 1500);
        return () => {
            alive = false;
            clearTimeout(timer);
        };
    }, [soloLevel, game?._id, userId]);

    const onRowClick = (data, key) => {
        let players;
        switch (key){
            case 'assignGreen':
                players = game.players.map(item => {
                    if(item.userId === data.userId){
                        return {...item, team: 'Green'}
                    }
                    return item;
                })
                dispatch(setPlayers(game._id, players));
                break;
            case 'assignBlue': 
                players = game.players.map(item => {
                    if(item.userId === data.userId){
                        return {...item, team: 'Blue'}
                    }
                    return item;
                })
                dispatch(setPlayers(game._id, players));
                break;
            case 'assignRed':
                players = game.players.map(item => {
                    if(item.userId === data.userId){
                        return {...item, team: 'Red'}
                    }
                    return item;
                })
                dispatch(setPlayers(game._id, players));
                break;
            default:
                console.log("Nothing to execute");
        }
    }

    const cursor = (pos) => {
        if(((!Boolean(game?.moves?.find(m => m.pos === pos)) && selectedCard === data[pos]) ||
            (['spades_jack', 'hearts_jack'].includes(selectedCard) && game?.moves?.find(m => m.pos === pos && m.team !== game?.players?.find(p => p.userId === userId)?.team?.toLowerCase())) ||
            (['clubs_jack', 'diamonds_jack'].includes(selectedCard) && !Boolean(game?.moves?.find(m => m.pos === pos)))) && game.currentPlayer === userId &&
            ![`0,0`, `0,9`, `9,0`, `9,9`].includes(pos)){
            return 'pointer';
        }
        return 'not-allowed';
    }

    const isBot = (playerId) => Boolean(game?.players?.find(p => p.userId === playerId)?.isBot);

    // Computer players never have to be asked, so only the people count.
    const rematchState = () => {
        const rematch = game?.rematch;
        const humans = (game?.players || []).filter(p => !p.isBot);
        const readyIds = rematch?.readyUserIds || [];
        const declinedIds = rematch?.declinedUserIds || [];
        const online = presence?.online || [];
        const status = (player) => {
            if(player.userId === rematch?.hostUserId){
                return 'host';
            }
            if(readyIds.includes(player.userId)){
                return 'ready';
            }
            if(declinedIds.includes(player.userId)){
                return 'declined';
            }
            return online.includes(player.userId) ? 'waiting' : 'away';
        };
        return {
            open: Boolean(rematch?.gameId) && rematch?.started !== true,
            isHost: rematch?.hostUserId === userId,
            iAmReady: readyIds.includes(userId),
            iDeclined: declinedIds.includes(userId),
            host: humans.find(p => p.userId === rematch?.hostUserId) || null,
            people: humans.map(player => ({player, status: status(player)})),
            ready: humans.filter(p => readyIds.includes(p.userId)).length,
            total: humans.length
        };
    };

    const answerRematch = (accept) => {
        const state = rematchState();
        if(accept){
            dispatch(requestRematch(params.gameId, userId, state.isHost && state.iAmReady));
            logEvent(analytics, state.isHost && state.iAmReady ? 'rematch_forced' : 'rematch_requested', {userId, gameId: params.gameId});
        } else {
            dispatch(declineRematch(params.gameId, userId));
            logEvent(analytics, 'rematch_declined', {userId, gameId: params.gameId});
        }
    };

    const rematchLabel = () => {
        const state = rematchState();
        if(!state.open){
            return 'Rematch';
        }
        const tally = state.total > 1 ? ` (${state.ready}/${state.total})` : '';
        if(!state.iAmReady){
            return `Join Rematch${tally}`;
        }
        // Kept short: a longer label wraps the top bar onto two lines.
        return state.isHost ? `Start Rematch${tally}` : `Ready${tally}`;
    };

    // 'hearts_10' -> '10 of Hearts', which is how a player would say it.
    const cardLabel = (card) => {
        if(!card || card === 'blank_card'){
            return 'a free corner';
        }
        const [suit, ...rest] = String(card).split('_');
        const value = rest.join(' ').replace('ace simple', 'ace');
        const title = (word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`;
        return `${title(value)} of ${title(suit)}`;
    };

    // After a one-eyed jack the newest entry in moves is an older chip rather
    // than the latest event, so the ring is held back while the removal marker
    // is the thing worth looking at.
    const lastMovePos = () => {
        const moves = game?.moves || [];
        if(removal || moves.length === 0){
            return null;
        }
        return moves[moves.length - 1].pos;
    };

    const playerLabel = (playerId) => {
        const player = game?.players?.find(p => p.userId === playerId);
        if(!player){
            return '';
        }
        return player.isBot ? `${player.name} (Computer)` : player.name;
    };

    // A card is dead once both of its board cells are taken. The official rule
    // lets you turn it in for a new one, which also stops a hand of dead cards
    // from freezing the game.
    const deadCards = () => {
        const myCards = game?.players?.find(p => p.userId === userId)?.inHandCards || [];
        const taken = new Set((game?.moves || []).map(m => m.pos));
        return myCards.filter(card => {
            if(String(card).includes('jack')){
                return false;
            }
            const cells = Object.keys(data).filter(pos => data[pos] === card);
            return cells.length > 0 && cells.every(pos => taken.has(pos));
        });
    };

    const getWon = () => {
        if(game?.isDraw){
            return 'Game Drawn - no moves left';
        }

        const noOfTeams = game?.noOfTeams;
        if(noOfTeams === 2){
            if(game.completedSequences.red === 2){
                let players = game.players.filter(i => i.team === 'Red').map(item => item.name);
                return `Red Team(${players.join(', ')}) Won`;
            } else if(game.completedSequences.blue === 2){
                let players = game.players.filter(i => i.team === 'Blue').map(item => item.name);
                return `Blue Team(${players.join(', ')}) Won`;
            } else if(game.completedSequences.green === 2){
                let players = game.players.filter(i => i.team === 'Green').map(item => item.name);
                return `Green Team(${players.join(', ')}) Won`;
            }
        } else if(noOfTeams === 3){
            if(game.completedSequences.red === 1){
                let players = game.players.filter(i => i.team === 'Red').map(item => item.name);
                return `Red Team(${players.join(', ')}) Won`;
            } else if(game.completedSequences.blue === 1){
                let players = game.players.filter(i => i.team === 'Blue').map(item => item.name);
                return `Blue Team(${players.join(', ')}) Won`;
            } else if(game.completedSequences.green === 1){
                let players = game.players.filter(i => i.team === 'Green').map(item => item.name);
                return `Green Team(${players.join(', ')}) Won`;
            }
        }
    }

    // The "last seen" label counts up in seconds. Polling used to re-render the
    // board every second as a side effect; this is a local timer that does the
    // same without touching the network, and only while someone is away.
    const [, setClockTick] = useState(0);
    const someoneOffline = Boolean(game?.players?.some(player => !presence?.online?.includes(player.userId)));
    useEffect(() => {
        if(!someoneOffline){
            return;
        }
        const timer = setInterval(() => setClockTick(tick => tick + 1), 1000);
        return () => clearInterval(timer);
    }, [someoneOffline])

    // Turn timer. The deadline comes from the server; serverNow lets the
    // countdown line up even when this computer's clock is off.
    const clockOffset = useRef(0);
    useEffect(() => {
        if(game?.serverNow){
            clockOffset.current = game.serverNow - Date.now();
        }
    }, [game]);
    const onTheClock = game?.status === 'START' && Boolean(game?.turnDeadline);
    useEffect(() => {
        if(!onTheClock){
            return;
        }
        const timer = setInterval(() => setClockTick(tick => tick + 1), 250);
        return () => clearInterval(timer);
    }, [onTheClock]);
    const secondsLeft = onTheClock
        ? Math.max(0, Math.ceil((new Date(game.turnDeadline).getTime() - (Date.now() + clockOffset.current)) / 1000))
        : null;

    // Say so when a turn runs out, and when a team completes a sequence. Only
    // changes seen while watching are announced, not whatever was true on load.
    const announced = useRef({gameId: null, timeout: null, sequences: null});
    useEffect(() => {
        if(!game?._id){
            return;
        }
        const timeoutKey = game.lastTimeout?.at ? String(game.lastTimeout.at) : 'none';
        const sequences = {...(game.completedSequences || {})};
        const seen = announced.current;
        if(seen.gameId !== game._id){
            announced.current = {gameId: game._id, timeout: timeoutKey, sequences};
            return;
        }

        if(seen.timeout !== timeoutKey && game.lastTimeout?.at){
            const t = game.lastTimeout;
            const who = t.userId === userId ? 'You' : t.name;
            dispatch(setToastId(`timeout-${timeoutKey}`));
            dispatch(setType('warning'));
            dispatch(setBody(t.replacedBy
                ? `${who} missed three turns in a row, so ${t.replacedBy} (computer) has taken the seat.`
                : `${who} ran out of time, so the turn passed on.`));
        }

        const needed = game.noOfTeams === 3 ? 1 : 2;
        const mine = String(game.players?.find(p => p.userId === userId)?.team || '').toLowerCase();
        Object.keys(sequences).forEach(team => {
            const before = (seen.sequences || {})[team] || 0;
            if(game.status === 'START' && sequences[team] > before){
                const left = needed - sequences[team];
                const label = team === mine ? 'Your team' : `${team.charAt(0).toUpperCase()}${team.slice(1)}`;
                dispatch(setToastId(`sequence-${team}-${sequences[team]}`));
                dispatch(setType(team === mine ? 'success' : 'warning'));
                dispatch(setBody(`${label} completed a sequence!${left > 0 ? ` ${left} more to win.` : ''}`));
            }
        });
        announced.current = {gameId: game._id, timeout: timeoutKey, sequences};
    }, [game, userId, dispatch]);

    // Presence comes from the socket room, so an absent player is known
    // immediately rather than inferred from a stale poll timestamp.
    const showTime = (playerId) => {
        if(!Boolean(playerId) || presence?.online?.includes(playerId)){
            return '';
        }
        const lastSeen = presence?.lastSeen?.[playerId] || game?.lastUpdated?.[playerId];
        if(!Boolean(lastSeen)){
            return '(offline)';
        }
        return `(Last seen ${Math.round((new Date().getTime() - new Date(lastSeen).getTime()) / 1000)} seconds ago)`;
    }


    // The video call. Offered to a seated person once there is someone else
    // human to talk to; the board is desktop-only, so the call is too.
    const mySeat = game?.players?.find(p => p.userId === userId);
    const humanCount = (game?.players || []).filter(p => !p.isBot).length;
    const call = useCall(params.gameId, !isMobile && Boolean(mySeat) && !mySeat.isBot && humanCount >= 2, userId);
    carryCall.current = call.carryTo;
    // The call enlarged over the table. Leaving the call puts the table back.
    const [enlarged, setEnlarged] = useState(false);
    const stageOpen = enlarged && isInCall(call);
    const enlarge = useCallback(() => setEnlarged(true), []);
    const shrink = useCallback(() => setEnlarged(false), []);
    useEffect(() => {
        if(!isInCall(call) && enlarged){
            setEnlarged(false);
        }
    }, [call, enlarged]);

    // When the turn moves on, bring that player's row into view if the
    // Players list has been scrolled. Only the panel scrolls, never the page.
    const panelScroll = useRef(null);
    // Which edges of the scrolling part have more beyond them, for the fade
    // that shows it scrolls (scrollbars are often hidden until used).
    const [overflow, setOverflow] = useState({ above: false, below: false });
    const checkOverflow = useCallback(() => {
        const el = panelScroll.current;
        if(!el){
            return;
        }
        const above = el.scrollTop > 2;
        const below = el.scrollTop + el.clientHeight < el.scrollHeight - 2;
        setOverflow(current => (current.above === above && current.below === below ? current : { above, below }));
    }, []);
    // The window changing size...
    useEffect(() => {
        const el = panelScroll.current;
        if(!el || typeof ResizeObserver === 'undefined'){
            return undefined;
        }
        const observer = new ResizeObserver(checkOverflow);
        observer.observe(el);
        return () => observer.disconnect();
    }, [checkOverflow, isMobile]);
    // ...and the content changing (rows, pictures, the game ending). Cheap,
    // and only sets state when an edge actually changes.
    useEffect(checkOverflow);
    useEffect(() => {
        const container = panelScroll.current;
        const row = container?.querySelector('.player.on-turn');
        if(!container || !row){
            return;
        }
        const box = container.getBoundingClientRect();
        const r = row.getBoundingClientRect();
        if(r.top < box.top){
            container.scrollTop -= box.top - r.top + 8;
        } else if(r.bottom > box.bottom){
            container.scrollTop += r.bottom - box.bottom + 8;
        }
    }, [game?.currentPlayer]);
    const [soundBlocked, setSoundBlocked] = useState(false);
    const blockedSound = useCallback(() => setSoundBlocked(true), []);
    const unblockSound = () => {
        document.querySelectorAll('.call-thumb:not(.self) video').forEach(v => v.play().catch(() => {}));
        setSoundBlocked(false);
    };

    if (isMobile) {
        return (
            <Grid container style={{ minHeight: '100vh', backgroundColor: '#0B2A21', flexDirection: 'column' }}>
                {/* Header */}
                <Grid item style={{ padding: '20px', textAlign: 'center', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                    <Typography variant='h4' style={{ color: '#fff', fontWeight: 700 }}>
                        Play Sequence
                    </Typography>
                    <Typography variant='body2' style={{ color: 'rgba(255,255,255,0.7)', marginTop: 4 }}>
                        The Classic Board Game Online
                    </Typography>
                </Grid>

                {/* Desktop notice */}
                <Grid item style={{ padding: '20px', textAlign: 'center' }}>
                    <Grid style={{ backgroundColor: '#E2B65B', color: '#16201C', borderRadius: '10px', padding: '16px' }}>
                        <Typography variant='h6' style={{ color: '#16201C', fontWeight: 700, marginBottom: 8 }}>
                            Play on a bigger screen
                        </Typography>
                        <Typography variant='body2' style={{ color: 'rgba(22, 32, 28, 0.85)' }}>
                            The Sequence game board requires a wider screen to display all 100 board spaces and your card hand. Please open this site on a desktop or laptop computer, or rotate your tablet to landscape mode.
                        </Typography>
                    </Grid>
                </Grid>

                {/* About the game */}
                <Grid item style={{ padding: '0 20px 20px' }}>
                    <Typography variant='h6' style={{ color: '#fff', fontWeight: 600, marginBottom: 8 }}>
                        About Sequence
                    </Typography>
                    <Typography variant='body2' style={{ color: 'rgba(255,255,255,0.8)', lineHeight: 1.7, marginBottom: 12 }}>
                        Sequence is an exciting strategy board game that combines card playing with board game mechanics. Players take turns playing cards from their hand and placing chips on corresponding spaces on a 10 by 10 game board. The goal is to form rows of five connected chips before your opponents do.
                    </Typography>
                    <Typography variant='body2' style={{ color: 'rgba(255,255,255,0.8)', lineHeight: 1.7, marginBottom: 12 }}>
                        Play Sequence brings this classic board game experience to your browser for free. Create a game room, invite friends with a simple link, and enjoy real-time multiplayer gameplay with built-in chat and optional video calling. No accounts or downloads required.
                    </Typography>
                    <Typography variant='body2' style={{ color: 'rgba(255,255,255,0.8)', lineHeight: 1.7 }}>
                        The game supports 2 to 12 players across 2 or 3 teams, with special Jack cards that act as wild cards or allow you to remove opponent chips. Master the strategy of corner sequences, card counting, and defensive blocking to become a Sequence champion.
                    </Typography>
                </Grid>

                {/* Navigation links */}
                <Grid item style={{ padding: '0 20px 20px' }}>
                    <Typography variant='h6' style={{ color: '#fff', fontWeight: 600, marginBottom: 12 }}>
                        Explore
                    </Typography>
                    {[
                        { label: 'How to Play', path: '/how-to-play', desc: 'Complete rules and game guide' },
                        { label: 'Strategy Guide', path: '/strategy', desc: 'Tips and tactics to win' },
                        { label: 'FAQ', path: '/faq', desc: 'Answers to common questions' },
                        { label: 'About Us', path: '/about', desc: 'Our mission and platform features' },
                        { label: 'Contact', path: '/contact', desc: 'Get in touch with our team' },
                    ].map((link) => (
                        <Button
                            key={link.path}
                            component={Link}
                            to={link.path}
                            fullWidth
                            style={{
                                justifyContent: 'flex-start',
                                color: '#fff',
                                textTransform: 'none',
                                padding: '10px 16px',
                                marginBottom: 4,
                                backgroundColor: 'rgba(255,255,255,0.05)',
                                borderRadius: 8,
                            }}
                        >
                            <Grid>
                                <Typography variant='body1' style={{ fontWeight: 600, textAlign: 'left' }}>{link.label}</Typography>
                                <Typography variant='caption' style={{ color: 'rgba(255,255,255,0.6)', textAlign: 'left', display: 'block' }}>{link.desc}</Typography>
                            </Grid>
                        </Button>
                    ))}
                </Grid>

                {/* Legal links */}
                <Grid item style={{ padding: '20px', borderTop: '1px solid rgba(255,255,255,0.1)', textAlign: 'center' }}>
                    <Grid style={{ display: 'flex', justifyContent: 'center', gap: 16, flexWrap: 'wrap' }}>
                        <Button component={Link} to="/privacy-policy" size="small" style={{ color: 'rgba(255,255,255,0.5)', textTransform: 'none', fontSize: '0.75rem' }}>Privacy Policy</Button>
                        <Button component={Link} to="/terms" size="small" style={{ color: 'rgba(255,255,255,0.5)', textTransform: 'none', fontSize: '0.75rem' }}>Terms of Service</Button>
                    </Grid>
                    <Typography variant='caption' style={{ color: 'rgba(255,255,255,0.3)', display: 'block', marginTop: 8 }}>
                        &copy; {new Date().getFullYear()} Play Sequence. Sequence is a registered trademark of Jax Ltd.
                    </Typography>
                </Grid>
            </Grid>
        );
    }

    const me = game?.players?.find(p => p.userId === userId);
    const myHand = me?.inHandCards || [];
    const myTurn = game?.status === 'START' && game?.currentPlayer === userId;
    // Only a card that is still in hand drives the board highlight.
    const activeCard = myTurn && myHand.includes(selectedCard) ? selectedCard : null;
    const selectedIndex = activeCard ? myHand.indexOf(activeCard) : -1;
    const moveAt = (pos) => game?.moves?.find(m => m.pos === pos);
    const teamColor = (team) => chipColor(team) || '#8A958F';

    const turnText = () => {
        if(game?.status === 'COMPLETED'){
            return outcome()?.title || getWon();
        }
        if(game?.status === 'START'){
            return game.currentPlayer === userId ? 'Your turn' : `${playerLabel(game.currentPlayer)}'s turn`;
        }
        return 'Waiting for players';
    };

    const handHint = () => {
        if(!myTurn){
            return game?.status === 'START' ? 'You can pick a card when it is your turn.' : '';
        }
        if(!activeCard){
            return 'Pick a card to play.';
        }
        if(['spades_jack', 'hearts_jack'].includes(activeCard)){
            return !hintsOn && miss ? "That chip can't be taken off." : "One-eyed jack: pick an opponent's chip to remove.";
        }
        if(['clubs_jack', 'diamonds_jack'].includes(activeCard)){
            return !hintsOn && miss ? 'That space is taken.' : 'Two-eyed jack: place a chip on any open space.';
        }
        if(!hintsOn && miss){
            return `The ${cardLabel(activeCard)} can't go there.`;
        }
        // "an 8", "an Ace", "a 7".
        const label = cardLabel(activeCard);
        return hintsOn ? `Place it on a highlighted ${label}.` : `Place it on ${/^(8|A)/.test(label) ? 'an' : 'a'} ${label}.`;
    };

    const rulesText = (full) => {
        const seconds = game?.settings?.turnSeconds;
        const board = game?.settings?.board === 'shuffled' ? 'Shuffled' : 'Classic';
        const highlights = game?.settings?.hints === false;
        if(full){
            return `${board} board, ${seconds > 0 ? `${seconds < 60 ? `${seconds}-second` : `${seconds / 60}-minute`} turns` : 'no turn timer'}${highlights ? ', no highlights' : ''}`;
        }
        return `${board} · ${seconds > 0 ? `${seconds < 60 ? `${seconds}s` : `${seconds / 60} min`} turns` : 'no timer'}${highlights ? ' · no highlights' : ''}`;
    };

    // Before joining: who is already on the call.
    const onCallNow = (playerId) => !isInCall(call) && call.participants.some(p => p.userId === playerId);

    const clockText = (seconds) => `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;

    const playerMeta = (player) => {
        if(game?.status === 'START' && game?.currentPlayer === player.userId){
            if(player.isBot){
                return 'Thinking…';
            }
            return secondsLeft !== null ? clockText(secondsLeft) : 'Playing';
        }
        if(player.isBot){
            return 'Computer';
        }
        return showTime(player.userId).replace(/[()]/g, '');
    };

    const siteLinks = [
        { label: 'Leaderboard', path: '/leaderboard' },
        { label: 'How to Play', path: '/how-to-play' },
        { label: 'Strategy', path: '/strategy' },
        { label: 'FAQ', path: '/faq' },
        { label: 'About', path: '/about' },
        { label: 'Contact', path: '/contact' },
    ];

    // Who won, from the finished game itself.
    const outcome = () => {
        if(game?.status !== 'COMPLETED'){
            return null;
        }
        const iPlayed = Boolean(me);
        const moveCount = game.history?.length || game.moves?.length || 0;
        if(game.isDraw){
            return {draw: true, iPlayed, moveCount, title: "It's a draw", detail: 'Nobody could make another move.'};
        }
        const needed = game.noOfTeams === 3 ? 1 : 2;
        const key = Object.keys(game.completedSequences || {}).find(team => game.completedSequences[team] >= needed);
        const winners = (game.players || []).filter(p => String(p.team).toLowerCase() === key);
        const losers = (game.players || []).filter(p => String(p.team).toLowerCase() !== key);
        const iWon = winners.some(p => p.userId === userId);
        const team = winners[0]?.team || key;
        const winnerNames = winners.map(p => p.userId === userId ? 'You' : p.name).join(' & ');
        const loserNames = losers.map(p => p.userId === userId ? 'you' : p.name).join(' & ');
        return {
            iWon, iPlayed, team, moveCount,
            title: iWon ? 'You won!' : iPlayed ? `${team} team won` : `${team} team won`,
            detail: `${winnerNames} beat ${loserNames} in ${moveCount} moves.`,
            shareText: iWon
                ? `I just beat ${losers.map(p => p.name).join(' & ')} at Sequence in ${moveCount} moves. Think you can beat me?`
                : `${winners.map(p => p.name).join(' & ')} won a game of Sequence in ${moveCount} moves. Watch it back:`
        };
    };
    const result = outcome();
    const rematch = rematchState();
    // A rematch in the air takes over the result dialog, even if that was
    // closed: everyone needs to see it and answer.
    const rematchView = Boolean(result?.iPlayed) && rematch.open && !rematch.iDeclined
        && rematchHiddenFor !== String(game?.rematch?.gameId);
    const resultDialogOpen = Boolean(result) && result.iPlayed && resultClosedFor !== game?._id;
    const rematchSummary = () => {
        const waiting = rematch.people.filter(p => p.status === 'waiting').map(p => p.player.name);
        const declined = rematch.people.filter(p => p.status === 'declined').map(p => p.player.name);
        if(declined.length > 0 && rematch.isHost){
            return `${declined.join(' & ')} said no. Play on with the computer in ${declined.length === 1 ? 'that seat' : 'those seats'}, or wait for them to change their mind.`;
        }
        if(waiting.length > 0){
            return `Waiting for ${waiting.join(' & ')}. It starts as soon as everyone accepts.`;
        }
        return 'Starting…';
    };
    const challenger = game?.challenge ? game?.players?.[0] : null;

    const inCall = isInCall(call);
    const turnPlayer = game?.status === 'START' ? game?.players?.find(p => p.userId === game.currentPlayer) : null;
    // How many sequences each team has, of how many it needs.
    const scoreline = (game?.status === 'START' || game?.status === 'COMPLETED') && game?.noOfTeams > 0
        ? <div className="scoreline" aria-label="Sequences completed">
            {[...new Set(game.players.map(p => p.team).filter(Boolean))].map(team => {
                const done = (game.completedSequences || {})[String(team).toLowerCase()] || 0;
                const needed = game.noOfTeams === 3 ? 1 : 2;
                return (
                    <span key={team} className={`score${done > 0 ? ' scored' : ''}`} style={{'--c': teamColor(team)}}>
                        <span className="chip" style={{'--c': teamColor(team)}} aria-hidden="true"/>
                        <span className="score-team">{team}</span>
                        <span className="score-pips" aria-hidden="true">
                            {Array.from({ length: needed }).map((_, i) => <span key={i} className={i < done ? 'pip done' : 'pip'}/>)}
                        </span>
                        <span className="score-count">{`${done} of ${needed}`}</span>
                    </span>
                );
            })}
        </div>
        : null;

    const quietButton = {
        color: tokens.parchment,
        borderColor: 'rgba(243, 238, 226, 0.28)',
        '&:hover': { borderColor: tokens.parchment, backgroundColor: 'rgba(243, 238, 226, 0.08)' },
    };

    return (
        <div className="table" style={{'--table': color}}>
            <IncomingCall call={call}/>
            {stageOpen && <CallStage call={call} players={game?.players} me={me} onClose={shrink}
                turn={game?.status === 'START' ? { text: turnText(), mine: myTurn, clock: secondsLeft !== null ? clockText(secondsLeft) : null } : null}
                soundBlocked={soundBlocked} onUnblockSound={unblockSound}/>}
            <header className="topbar">
                <div className="brand">
                    <Link to="/" className="brand-name" aria-label="Play Sequence home">
                        <span className="brand-mark" aria-hidden="true"><span/><span/><span/></span>
                        Play Sequence
                    </Link>
                    <nav className="site-links" aria-label="Site">
                        {siteLinks.map((link) => (
                            <Link key={link.path} to={link.path}>{link.label}</Link>
                        ))}
                    </nav>
                </div>
                <div className="turn" aria-live="polite">
                    <div className={`turn-banner${game?.status === 'COMPLETED' ? ' done' : myTurn ? ' mine' : ''}${secondsLeft !== null && secondsLeft <= 10 ? ' urgent' : ''}`}>
                        {turnText()}
                        {secondsLeft !== null && <span className="turn-clock" aria-label={`${secondsLeft} seconds left`}>{clockText(secondsLeft)}</span>}
                    </div>
                    {secondsLeft !== null && game?.settings?.turnSeconds > 0 &&
                        <div className="turn-progress" aria-hidden="true">
                            <span style={{width: `${Math.min(100, (secondsLeft / game.settings.turnSeconds) * 100)}%`}}/>
                        </div>}
                    {!connected && <div className="reconnecting">Reconnecting…</div>}
                </div>
                <div className="actions">
                    {game?.status === 'COMPLETED' && game?.players?.some(p => p.userId === userId) &&
                        <Button
                            variant="contained"
                            color="secondary"
                            disabled={rematchState().open && rematchState().iAmReady && !rematchState().isHost}
                            onClick={() => {
                                const state = rematchState();
                                if(state.open && !state.iDeclined && rematchHiddenFor === String(game?.rematch?.gameId) && !(state.isHost && state.iAmReady)){
                                    // Bring the rematch back up rather than answer it blind.
                                    setRematchHiddenFor(null);
                                    return;
                                }
                                answerRematch(true);
                            }}
                        >{rematchLabel()}</Button>}
                    <Tooltip title="Table colour">
                        <IconButton aria-label="Change table colour" sx={{color: tokens.parchment}} onClick={() => {
                            setOpenColorPicker(true);
                        }}><PaletteOutlined/></IconButton>
                    </Tooltip>
                    <Tooltip title="The game screen as it looked before the redesign" describeChild>
                        <Button variant="outlined" sx={quietButton} onClick={() => {
                            setPrefersClassic(true);
                            logEvent(analytics, 'classic_look_chosen', {userId, gameId: params.gameId || null});
                            navigate(gamePath(params.gameId, true));
                        }}>Classic look</Button>
                    </Tooltip>
                    <Button variant="outlined" sx={quietButton} onClick={() => {
                        setFeedbackForm({open: true});
                    }}>Feedback</Button>
                    <Button variant="contained" color="secondary" onClick={() => {
                        navigate('/');
                        window.location.reload();
                    }}>New game</Button>
                    <AccountButton/>
                </div>
            </header>
            <Dialog open={opneColorPicker}>
                <DialogTitle>Table colour</DialogTitle>
                <DialogContent>
                    <ColorPicker color={color} onChange={(c) => setColor(c.hex) }/>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setColor(tokens.felt)}>Reset</Button>
                    <Button onClick={() => setOpenColorPicker(false)}>Done</Button>
                </DialogActions>
            </Dialog>
            <PVForms 
                fields={[
                    {
                        label: 'Report Type',
                        property: 'type',
                        required: true,
                        type: 'SELECT',
                        listValues: [
                            {
                                label: 'Bug',
                                value: 'bug'
                            },
                            {
                                label: 'Feature Request',
                                value: 'feature'
                            },
                            {
                                label: 'Other',
                                value: 'other'
                            }
                        ],
                        listLabelKey: 'label',
                        listValueKey: 'value'
                    },
                    {
                        label: 'Description',
                        property: 'description',
                        required: true,
                        type: 'TEXTAREA',
                        rows: 5
                    }
                ]}
                record={feedbackForm}
                onChangeRecord={setFeedbackForm}
                title="Provide Feedback"
                saveLabel={'Submit'}
                onSaveClicked={() => {
                    dispatch(sendFeedback(feedbackForm.editRecord, () => {
                        setFeedbackForm({});
                    }))
                }}
            />
            <Dialog open={game?.challenge === true && game?.status === 'INIT' && Boolean(me)} fullWidth maxWidth="sm">
                <DialogTitle>Challenge a friend</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" color="text.secondary">
                        Send this link to anyone. The game starts as soon as they open it and enter their name.
                    </Typography>
                    <ShareButtons
                        url={challengeLink(game?._id)}
                        text={`${me?.name || 'A friend'} challenged you to a game of Sequence. Think you can win?`}
                        kind="challenge"
                        itemId={game?._id}
                    />
                    <div className="waiting">
                        <CircularProgress size={18} thickness={5} color="primary"/>
                        <span>Waiting for your friend to join…</span>
                    </div>
                </DialogContent>
                <DialogActions sx={{px: 3, pb: 2.5, gap: 1}}>
                    <Button onClick={() => navigate('/')}>Cancel</Button>
                    <span style={{flex: 1}}/>
                    <Button onClick={() => dispatch(addBot(params.gameId, 'medium'))}>Play the computer instead</Button>
                </DialogActions>
            </Dialog>

            {result?.iWon && resultDialogOpen && <div className="chip-rain" aria-hidden="true">
                {Array.from({ length: 36 }).map((_, i) => (
                    <span key={i} className="chip" style={{
                        // Spread across the screen with a little jitter, never random
                        // per render so the burst doesn't restart on every update.
                        '--c': i % 4 === 3 ? tokens.brass : chipColor(me?.team),
                        '--x': `${(i * 37) % 100}vw`,
                        '--delay': `${(i % 9) * 90}ms`,
                        '--dur': `${1700 + (i % 5) * 260}ms`,
                        '--spin': `${(i % 2 ? 1 : -1) * (360 + (i % 4) * 120)}deg`,
                        '--size': `${26 + (i % 3) * 10}px`
                    }}/>
                ))}
            </div>}

            <Dialog open={resultDialogOpen || rematchView} fullWidth maxWidth="md" PaperProps={{className: result?.iWon && !rematchView ? 'result-paper won' : 'result-paper', sx: {overflow: 'hidden'}}}>
                {result && rematchView && <>
                    <div className="result-banner rematch">
                        <ReplayRounded className="result-trophy"/>
                        <h2>{rematch.isHost ? 'Rematch challenge sent' : `${rematch.host?.name || 'Someone'} wants a rematch`}</h2>
                        <p>{rematchSummary()}</p>
                        <CallStrip call={call} players={game?.players} me={me} soundBlocked={soundBlocked} onUnblockSound={unblockSound}/>
                    </div>
                    <DialogContent sx={{pt: '20px !important'}}>
                        <ul className="rematch-people" aria-label="Who is in">
                            {rematch.people.map(({player, status}) => (
                                <li key={player.userId} className={`rematch-person ${status}`}>
                                    <span className="chip" style={{'--c': teamColor(player.team)}} aria-hidden="true"/>
                                    <span className="rematch-name">{player.name}{player.userId === userId ? ' (you)' : ''}</span>
                                    <span className="rematch-status">
                                        {(status === 'host' || status === 'ready') && <CheckCircleRounded fontSize="small"/>}
                                        {status === 'declined' && <HighlightOffRounded fontSize="small"/>}
                                        {status === 'waiting' && <HourglassEmptyRounded fontSize="small"/>}
                                        {REMATCH_STATUS[status]}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </DialogContent>
                    <DialogActions sx={{px: 3, pb: 2.5, gap: 1, flexWrap: 'wrap'}}>
                        <Button onClick={() => setRematchHiddenFor(String(game?.rematch?.gameId))}>Hide</Button>
                        <span style={{flex: 1}}/>
                        {rematch.isHost && rematch.ready < rematch.total && <Button variant="contained" onClick={() => answerRematch(true)}>
                            {rematch.people.some(p => p.status === 'declined' || p.status === 'away') ? 'Play on with the computer' : 'Start without waiting'}
                        </Button>}
                        {!rematch.isHost && !rematch.iAmReady && <>
                            <Button variant="outlined" color="error" onClick={() => answerRematch(false)}>Decline</Button>
                            <Button variant="contained" size="large" onClick={() => answerRematch(true)}>Accept rematch</Button>
                        </>}
                        {!rematch.isHost && rematch.iAmReady && <Button variant="contained" disabled startIcon={<CircularProgress size={14} color="inherit"/>}>Waiting for the others</Button>}
                    </DialogActions>
                </>}
                {result && !rematchView && <>
                    <div className={`result-banner${result.iWon ? ' won' : ''}`}>
                        <EmojiEventsOutlined className="result-trophy"/>
                        <h2>{result.title}</h2>
                        <p>{result.detail}</p>
                        {ranks && <RankStrip ranks={ranks} won={soloGame?.won} onSignIn={() => signInWithGoogle(dispatch)}/>}
                        <CallStrip call={call} players={game?.players} me={me} soundBlocked={soundBlocked} onUnblockSound={unblockSound}/>
                    </div>
                    <DialogContent sx={{pt: '20px !important'}} className="result-body">
                        <ReplayClip
                            history={game?.history}
                            finalMoves={game?.moves}
                            layout={game?.layout}
                            gameId={game?._id}
                            onOpen={() => navigate(`/r/${game?._id}`)}
                        />
                        <div>
                            <Typography variant="h6" component="h3" sx={{fontSize: '1.05rem'}}>
                                {result.iWon ? 'Share your win' : 'Share this game'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Friends get a picture of the final board and can watch the game back.
                            </Typography>
                            <ShareButtons url={resultLink(game?._id)} text={result.shareText} kind="result" itemId={game?._id}/>
                        </div>
                    </DialogContent>
                    <DialogActions sx={{px: 3, pb: 2.5, gap: 1, flexWrap: 'wrap'}}>
                        <Button component={Link} to={`/r/${game?._id}`} startIcon={<ReplayRounded/>}>Watch replay</Button>
                        {soloGame && <Button component={Link} to={`/leaderboard?level=${soloGame.difficulty}`} startIcon={<LeaderboardRounded/>}>Leaderboard</Button>}
                        <span style={{flex: 1}}/>
                        <Button onClick={() => setResultClosedFor(game?._id)}>Close</Button>
                        <Button
                            variant="contained"
                            disabled={rematch.open && rematch.iAmReady && !rematch.isHost}
                            onClick={() => answerRematch(true)}
                        >{rematchLabel()}</Button>
                    </DialogActions>
                </>}
            </Dialog>

            {/* Only for people already seated: someone arriving by the link
                gets the name dialog first, which this one would cover. */}
            <Dialog open={(game?.status === 'INIT' || game?.status === 'WAITING') && Boolean(me) && game?.challenge !== true}>
                <DialogTitle>Team</DialogTitle>
                <DialogContent>
                    {game?.status === 'INIT' && <Typography variant="body1">Share this link with your friends, then pick teams below.</Typography>}
                    {game?.status === 'INIT' && <ShareButtons url={window.location.href} text="Join my game of Sequence:" kind="invite" itemId={game?._id}/>}
                    {game?.status === 'WAITING' && <Typography variant="body1">Looking for another player... if nobody joins in a few seconds, a computer player will take the seat and the game will start.</Typography>}
                    <PVTable 
                        columns={[
                            {
                                label: 'Name',
                                property: 'name'
                            },
                            {
                                label: 'Team',
                                property: 'team'
                            }
                        ]}
                        data={game.players}
                        clickMenu={[
                            {
                                key: 'assignGreen',
                                label: 'Assign Green',
                                enabled: (game?.noOfPlayers % 2 === 0 || game?.noOfPlayers % 3 === 0) && game?.players?.filter(item => item.team === 'Green').length < Math.floor(game?.noOfPlayers / 2)
                            },
                            {
                                key: 'assignBlue',
                                label: 'Assign Blue',
                                enabled: (game?.noOfPlayers % 2 === 0 || game?.noOfPlayers % 3 === 0) && game?.players?.filter(item => item.team === 'Blue').length < Math.floor(game?.noOfPlayers / 2)
                            },
                            {
                                key: 'assignRed',
                                label: 'Assign Red',
                                enabled: game?.noOfPlayers % 3 === 0
                            }
                        ]}
                        onRowClick={onRowClick}
                    />
                </DialogContent>
                <DialogActions style={{textAlign: 'center'}}>
                    {game?.players?.length < game?.noOfPlayers &&
                        <Button onClick={() => dispatch(addBot(params.gameId, 'medium'))}>Add Computer Player</Button>}
                    <Button onClick={start} disabled={!canStartGame()}>Start Game</Button>
                </DialogActions>
            </Dialog>

            <StartGame open={record?.open} onStart={startNewGame} onClose={() => setRecord({})} onClassic={() => {
                setPrefersClassic(true);
                logEvent(analytics, 'classic_look_chosen', {userId, gameId: null});
                navigate(gamePath(null, true));
            }}/>
            <PVForms 
                fields={[
                    {
                        label: 'Name',
                        property: 'name',
                        required: true
                    }
                ]}
                record={nameRecord}
                onChangeRecord={setNameRecord}
                title={challenger ? `${challenger.name} challenged you` : 'Your name'}
                saveLabel={challenger ? 'Accept and play' : 'Join Game'}
                onSaveClicked={join}
            />
            <main className="play">
                <div className={`board${activeCard && hintsOn ? ' aiming' : ''}`} role="grid" aria-label="Sequence board">
                    {Array.from({ length: 10 }).map((_, i) => Array.from({ length: 10 }).map((__, j) => {
                        const pos = `${i},${j}`;
                        const chip = moveAt(pos);
                        const playable = cursor(pos) === 'pointer';
                        // With highlights off, every space looks the same
                        // under the pointer too: the cursor gives nothing away.
                        const pointer = hintsOn ? cursor(pos) : (activeCard && data[pos] !== 'blank_card' ? 'pointer' : 'default');
                        const missed = miss?.pos === pos;
                        return (
                            <div
                                key={missed ? `${pos}-${miss.at}` : pos}
                                role="gridcell"
                                className={`cell${playable && hintsOn ? ' playable' : ''}${missed ? ' miss' : ''}${chip?.completedSequence ? ' in-sequence' : ''}${data[pos] === 'blank_card' ? ' corner' : ''}`}
                                style={{cursor: pointer, ...(chip?.completedSequence ? {'--seq': teamColor(chip.team)} : null)}}
                                onClick={() => move(pos)}
                            >
                                <img
                                    src={`https://d5hzzxzqjp9y1.cloudfront.net/cardfronts/${Object.keys(data).includes(pos) ? data[pos] : 'spades_ace_simple'}.png`}
                                    alt={cardLabel(data[pos])}
                                    draggable={false}
                                />
                                {chip && <div
                                    className={`chip${lastMovePos() === pos ? ' last-move' : ''}`}
                                    title={lastMovePos() === pos ? `Last move: ${playerLabel(game.moves[game.moves.length - 1].userId)} played the ${cardLabel(game.moves[game.moves.length - 1].card)}` : undefined}
                                    style={{'--c': teamColor(chip.team)}}
                                />}
                                {removal?.pos === pos && <div
                                    className="removed-chip"
                                    title={`A ${removal.team} chip was taken off this cell with a one-eyed jack`}
                                    style={{color: teamColor(removal.team)}}
                                />}
                            </div>
                        );
                    }))}
                </div>

                <aside className="panel">
                    {/* Pinned at the top: who you are, and the call controls,
                        so mute is never scrolled out of reach. */}
                    {me && !inCall && <section className="panel-section you">
                        <span className="chip" style={{'--c': teamColor(me.team)}} aria-hidden="true"/>
                        <div>
                            <div className="you-team">{me.team ? `Team ${me.team}` : 'No team yet'}</div>
                            <div className="you-name">{`Playing as ${me.name}`}</div>
                        </div>
                        <CallControls call={call} me={me} soundBlocked={soundBlocked} onUnblockSound={unblockSound}/>
                    </section>}

                    {/* On a call, the pictures take the place of that card
                        and the Players card; the score and whose turn it is
                        move onto them. */}
                    {me && inCall && <CallDock
                        call={call} players={game?.players} me={me}
                        turnId={game?.status === 'START' ? game?.currentPlayer : null}
                        turnTag={turnPlayer ? playerMeta(turnPlayer) : ''}
                        head={scoreline && <div className="call-dock-head" title={rulesText(true)}>{scoreline}</div>}
                        soundBlocked={soundBlocked} onUnblockSound={unblockSound} onBlocked={blockedSound} onEnlarge={enlarge}/>}

                    {/* Between the two pinned parts. It takes whatever height
                        is left and scrolls on its own, so a long Players list
                        (or a call) can never push the cards off screen. */}
                    <div className={`panel-scroll${overflow.above ? ' more-above' : ''}${overflow.below ? ' more-below' : ''}`} ref={panelScroll} onScroll={checkOverflow}>
                    {result && result.iPlayed && <section className="panel-section game-over">
                        <div className="game-over-title">{result.title}</div>
                        <div className="you-name">{result.detail}</div>
                        {ranks?.periods && Object.values(ranks.periods).some(p => p.rank) && <div className="you-name game-over-rank">
                            {`Ranked ${['day', 'week', 'month', 'all'].filter(k => ranks.periods[k].rank).map(k => `#${ranks.periods[k].rank} ${PERIOD_LABEL[k].toLowerCase()}`).join(' · ')} on ${DIFFICULTY_LABEL[ranks.difficulty]}`}
                        </div>}
                        <div className="game-over-actions">
                            <Button variant="contained" color="secondary" size="small" onClick={() => setResultClosedFor(null)}>
                                {result.iWon ? 'Share your win' : 'Share'}
                            </Button>
                            {soloGame && <Button variant="outlined" size="small" sx={quietButton} component={Link} to={`/leaderboard?level=${soloGame.difficulty}`} startIcon={<LeaderboardRounded/>}>Leaderboard</Button>}
                        </div>
                    </section>}

                    {game?.players?.length > 0 && !inCall && <section className="panel-section">
                        <div className="players-head">
                            <h2 className="panel-heading">Players</h2>
                            <span className="game-rules" title={rulesText(true)}>{rulesText(false)}</span>
                        </div>
                        {scoreline}
                        <ul className="players">
                            {game.players.map((player) => {
                                return (
                                <li key={player.userId} className={`player${game?.status === 'START' && game?.currentPlayer === player.userId ? ' on-turn' : ''}`}>
                                    <span className="chip" style={{'--c': teamColor(player.team)}} aria-hidden="true"/>
                                    <span className="player-name">
                                        {player.userId === userId ? `${player.name} (you)` : player.name}
                                        {onCallNow(player.userId) && <VideocamRounded className="on-call" titleAccess="On the call"/>}
                                    </span>
                                    <span className="player-meta">{playerMeta(player)}</span>
                                </li>
                                );
                            })}
                        </ul>
                    </section>}

                    {isBot(game?.currentPlayer) && game?.status === 'START' &&
                        <p className="thinking">{`${playerLabel(game?.currentPlayer)} is thinking…`}</p>}
                    </div>

                    {myHand.length > 0 && game?.status !== 'COMPLETED' && <section className="panel-section hand-section">
                        <div className="hand-head">
                            <h2 className="panel-heading">Your hand</h2>
                            {!hintsOn && <span className="hints-off" title="This game was set up without highlights, for everyone">No highlights</span>}
                        </div>
                        {handHint() && <p className={`hint${!hintsOn && miss ? ' miss' : ''}`} role={!hintsOn && miss ? 'alert' : undefined}>{handHint()}</p>}
                        <div className={`hand${selectedIndex >= 0 ? ' has-selection' : ''}`}>
                            {myHand.map((card, i) => {
                                // Spread the cards in an arc around the middle of the hand.
                                const d = i - (myHand.length - 1) / 2;
                                return (
                                <button
                                    key={`${card}-${i}`}
                                    type="button"
                                    className={`hand-card${i === selectedIndex ? ' selected' : ''}`}
                                    style={{'--d': d, '--r': `${d * 4}deg`, '--y': `${d * d * 2}px`}}
                                    disabled={game.currentPlayer !== userId}
                                    aria-pressed={i === selectedIndex}
                                    aria-label={cardLabel(card)}
                                    onClick={() => {
                                        if(game.currentPlayer === userId){
                                            setSelectedCard(card)
                                        }
                                    }}
                                >
                                    <img src={`https://d5hzzxzqjp9y1.cloudfront.net/cardfronts/${card}.png`} alt="" draggable={false}/>
                                </button>
                                );
                            })}
                        </div>
                        {game?.currentPlayer === userId && game?.status === 'START' && deadCards().length > 0 &&
                            <Button size="small" variant="outlined" sx={{...quietButton, mt: 1.5}}
                                onClick={() => dispatch(discardCard(params.gameId, userId, deadCards()[0]))}>
                                {`Turn in dead card (${cardLabel(deadCards()[0])})`}
                            </Button>}
                    </section>}
                </aside>
            </main>

            {/* SEO Content Section */}
            <section className="about-game">
                <div className="about-game-inner">
                    <h2>Play Sequence online, free, with friends</h2>
                    <p>
                        Welcome to Play Sequence, the free online platform where you can enjoy the classic Sequence board game with friends and family from anywhere in the world. Sequence is a strategy board game that combines card playing with board game mechanics on a 10 by 10 grid. Players take turns playing cards from their hand and placing chips on matching board spaces, racing to form rows of five connected chips in a horizontal, vertical, or diagonal line.
                    </p>
                    <p>
                        Our platform supports 2 to 12 players across 2 or 3 teams, making it perfect for both quick head-to-head matches and large group game nights. Create a game room, share the link with your friends, and start playing in seconds. No accounts, no downloads, and no fees. The game includes special Jack cards that serve as wild cards or allow you to remove opponent chips, adding layers of strategy to every match. Use the built-in chat to communicate with other players, or enable video calling for a more social experience.
                    </p>
                    <p>
                        Whether you are new to Sequence or a seasoned player, our platform provides everything you need for an authentic game experience. The game engine handles all rules automatically, including turn order, valid move checking, sequence detection, and win conditions. Visit our <Link to="/how-to-play" style={{color: tokens.brass}}>How to Play guide</Link> to learn the complete rules, or check out the <Link to="/strategy" style={{color: tokens.brass}}>Strategy Guide</Link> for expert tips on corner sequences, Jack card management, and defensive blocking techniques.
                    </p>
                </div>
            </section>

            <footer className="table-footer">
                <nav aria-label="Footer">
                    {[...siteLinks,
                        { label: 'Privacy Policy', path: '/privacy-policy' },
                        { label: 'Terms of Service', path: '/terms' },
                    ].map((link) => (
                        <Link key={link.path} to={link.path}>{link.label}</Link>
                    ))}
                </nav>
                <small>&copy; {new Date().getFullYear()} Play Sequence. All rights reserved. Sequence is a registered trademark of Jax Ltd.</small>
            </footer>
        </div>
    )
}

export default Board;